import type { FetchClient, RequestOptions } from '../../../lib/http/http.types';
import { toQueryString } from '../../../lib/url/urlUtils';
import type {
  AssignProfilePayload,
  CreatedUser,
  CreateUserPayload,
  PaginatedUsersResponse,
  PasswordLinkResponse,
  UpdateUserPasswordPayload,
  UpdateUserPayload,
  UpdateUserStatusPayload,
  User,
  UsersQueryParams,
} from '../userTypes';

/**
 * Factory API users — reçoit FetchClient en injection.
 *
 * Avantages :
 * - Testable sans mock global (on passe un FetchClient mock)
 * - Inchangé lors de la migration TanStack Query
 * - Centralise tous les paths de l'endpoint /users
 *
 * Usage :
 *   const [client] = useFetch();
 *   const api = useMemo(() => createUsersApi(client), [client]);
 */
export function createUsersApi(client: FetchClient) {
  return {
    getAll: (params?: UsersQueryParams, opts?: RequestOptions): Promise<PaginatedUsersResponse> => {
      const qs = new URLSearchParams();
      if (params?.page != null) qs.set('page', String(params.page));
      if (params?.limit != null) qs.set('limit', String(params.limit));
      if (params?.sortBy != null) qs.set('sortBy', params.sortBy);
      if (params?.sortOrder != null) qs.set('sortOrder', params.sortOrder);
      if (params?.query) qs.set('query', params.query);
      for (const [key, value] of Object.entries(params?.filters ?? {})) {
        qs.set(key, value);
      }
      const query = toQueryString(qs);
      return client.get<PaginatedUsersResponse>(`/users${query ? `?${query}` : ''}`, opts);
    },

    getById: (id: string, opts?: RequestOptions): Promise<User> =>
      client.get<User>(`/users/${id}`, opts),

    create: (payload: CreateUserPayload, opts?: RequestOptions): Promise<CreatedUser> =>
      client.post<CreatedUser>('/users', payload, opts),

    update: (id: string, payload: UpdateUserPayload, opts?: RequestOptions): Promise<User> =>
      client.patch<User>(`/users/${id}`, payload, opts),

    updateStatus: (
      id: string,
      payload: UpdateUserStatusPayload,
      opts?: RequestOptions,
    ): Promise<User> => client.patch<User>(`/users/${id}/status`, payload, opts),

    updatePassword: (
      id: string,
      payload: UpdateUserPasswordPayload,
      opts?: RequestOptions,
    ): Promise<User> => client.patch<User>(`/users/password/${id}`, payload, opts),

    assignProfile: (
      id: string,
      payload: AssignProfilePayload,
      opts?: RequestOptions,
    ): Promise<User> => client.put<User>(`/users/${id}/profile`, payload, opts),
    /** HOMCO-1393: no body — invalidates the password and e-mails a one-time link (admin only). */
    sendPasswordLink: (id: string, opts?: RequestOptions): Promise<PasswordLinkResponse> =>
      client.post<PasswordLinkResponse>(`/users/${id}/password-link`, undefined, opts),

    delete: (id: string, opts?: RequestOptions): Promise<void> =>
      client.delete<void>(`/users/${id}`, opts),
  };
}
